import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { useSaveToast } from '../ui/SaveToast';
import { DEFAULT_FLIGHT_SETTINGS, arrivalPickup, formatClock } from '../../lib/flightPickup';
import HoverHelp from '../shared/HoverHelp';

// FA-2: Admin Center > Flight tracking. Every airport rule is the company's own setting; the defaults are The Ride ATL's.
// FlightAware is paid, so this card also shows this month's calls against the company's cap.
const NUMBER_FIELDS = [
    ['arrival_buffer_domestic_minutes', 'Domestic arrival: pickup after scheduled arrival (minutes)', 0, 240],
    ['arrival_buffer_international_minutes', 'International arrival: pickup after scheduled arrival (minutes)', 0, 240],
    ['change_threshold_minutes', 'Suggest a new pickup when the flight moves by (minutes)', 1, 240],
    ['poll_window_hours', 'Start tracking this many hours before the flight', 1, 48],
    ['monthly_call_cap', 'Monthly FlightAware call limit', 0, 1000000],
    ['wait_start_domestic_minutes', 'Wait time starts after gate arrival, Domestic (minutes)', 0, 240],
    ['wait_start_international_minutes', 'Wait time starts after gate arrival, International (minutes)', 0, 240],
];
const EXAMPLE_ARRIVAL = '2026-01-15T15:42:00';

export default function FlightTrackingSettings({ orgId }) {
    const toast = useSaveToast();
    const [values, setValues] = useState({ ...DEFAULT_FLIGHT_SETTINGS });
    const [usage, setUsage] = useState(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        if (!orgId) { setBusy(false); return () => { active = false; }; }
        (async () => {
            try {
                const { data, error: failure } = await supabaseClient.from('org_flight_settings')
                    .select(Object.keys(DEFAULT_FLIGHT_SETTINGS).join(', ')).eq('org_id', orgId).maybeSingle();
                if (!active) return;
                if (failure) setError(`Flight tracking settings could not be loaded: ${failure.message}`);
                else setValues({ ...DEFAULT_FLIGHT_SETTINGS, ...(data || {}) });
                const summary = await supabaseClient.rpc('fa2_usage_summary');
                if (active && !summary.error) setUsage(summary.data || null);
            } catch (failure) {
                if (active) setError(`Flight tracking settings could not be loaded: ${failure.message}`);
            } finally {
                if (active) setBusy(false);
            }
        })();
        return () => { active = false; };
    }, [orgId]);

    const set = (key, value) => setValues((previous) => ({ ...previous, [key]: value }));

    async function save(event) {
        event.preventDefault();
        const row = { org_id: orgId };
        for (const [key, label, min, max] of NUMBER_FIELDS) {
            const number = Number(values[key]);
            if (!Number.isInteger(number) || number < min || number > max) {
                setError(`${label}: use a whole number from ${min} to ${max.toLocaleString()}.`);
                return;
            }
            row[key] = number;
        }
        row.pickup_rounding_minutes = Number(values.pickup_rounding_minutes);
        row.auto_apply_flight_changes = Boolean(values.auto_apply_flight_changes);
        row.client_delay_text_enabled = Boolean(values.client_delay_text_enabled);
        setBusy(true);
        setError('');
        try {
            const { error: failure } = await supabaseClient.from('org_flight_settings').upsert(row, { onConflict: 'org_id' });
            if (failure) throw failure;
            toast.saved();
        } catch (failure) {
            setError(`Flight tracking settings could not be saved: ${failure.message}`);
            toast.failed(`Flight tracking settings could not be saved: ${failure.message}`);
        } finally { setBusy(false); }
    }

    const example = arrivalPickup(new Date(EXAMPLE_ARRIVAL), 'domestic', values);
    const exampleIntl = arrivalPickup(new Date(EXAMPLE_ARRIVAL), 'international', values);

    return (
        <section className="rounded-lg border p-4 space-y-3" aria-label="Flight tracking">
            <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">Flight tracking</h3>
                <HoverHelp helpId="settings.flight-tracking" content={{ title: 'Flight tracking', body: 'Set arrival buffers, pickup rounding and the monthly FlightAware call limit for this company. Flight changes appear as suggestions unless auto-apply is enabled. Raising the monthly limit resumes tracking when it has paused at the cap.' }} />
            </div>
            {usage && (
                <p className="text-sm" data-testid="flight-usage">
                    This month: <strong>{Number(usage.calls || 0).toLocaleString()}</strong> of {Number(usage.cap || 0).toLocaleString()} FlightAware calls
                    {' · '}{Number(usage.trackedTrips || 0).toLocaleString()} flights being tracked.
                    {usage.paused && <strong className="block text-red-700">Tracking is paused: the monthly limit was reached. Raise the limit to resume.</strong>}
                </p>
            )}
            <form onSubmit={save} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                    {NUMBER_FIELDS.map(([key, label, min, max]) => (
                        <label key={key} className="block text-sm">{label}
                            <input type="number" min={min} max={max} step="1" required value={values[key]} disabled={busy}
                                onChange={(event) => set(key, event.target.value)} className="mt-1 block w-full rounded border px-2 py-1" />
                        </label>
                    ))}
                    <label className="block text-sm">Round the pickup up to the next
                        <select value={values.pickup_rounding_minutes} disabled={busy} onChange={(event) => set('pickup_rounding_minutes', event.target.value)}
                            className="mt-1 block w-full rounded border px-2 py-1">
                            {[1, 5, 10, 15].map((step) => <option key={step} value={step}>{step} minute{step === 1 ? '' : 's'}</option>)}
                        </select>
                    </label>
                </div>
                <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" checked={Boolean(values.auto_apply_flight_changes)} disabled={busy}
                        onChange={(event) => set('auto_apply_flight_changes', event.target.checked)} />
                    <span>Auto-apply flight changes <span className="block text-[var(--ink-muted)]">Off: dispatch approves each suggested pickup with one tap. On: the pickup moves by itself.</span></span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" checked={Boolean(values.client_delay_text_enabled)} disabled={busy}
                        onChange={(event) => set('client_delay_text_enabled', event.target.checked)} />
                    <span>Tell the client when their pickup is adjusted <span className="block text-[var(--ink-muted)]">Sent only after dispatch applies the change, through your normal client messages.</span></span>
                </label>
                <p className="text-sm" data-testid="flight-rule-example">
                    Example: a flight scheduled to land at {formatClock(new Date(EXAMPLE_ARRIVAL))} gets a {formatClock(example)} pickup (Domestic) or {formatClock(exampleIntl)} (International). Scheduled times only, never estimated or landed times.
                </p>
                <button className="ia-btn3d ia-btn3d--go" disabled={busy} type="submit">Save flight tracking</button>
            </form>
            <p className="text-sm">Only an Owner or Admin can change these. Departures are tracked and shown; their pickup times are still set by dispatch.</p>
            {error && <p role="alert" className="text-red-700">{error}</p>}
        </section>
    );
}
